import * as Yup from 'yup';
import { TenantSeniorityStatusEnum } from 'api/real-estate';
import { ValidationRules } from 'helpers/createFormConfig';
import { getTenantSeniorityStatus } from '../utils/tenantSeniority';
import { TenantFormValues } from '../types/tenantFormValues';
import { TenantFormFieldNames } from '../types/tenantFormFieldNames';

/**
 * Les exigences dépendent de l'ancienneté du locataire à la date de début du contrat :
 * nouveau → revenus obligatoires ; en place → déclaration « à jour de ses loyers » obligatoire.
 * D'où la factory : la date de début de contrat vient du risque, pas du formulaire.
 */
export const buildTenantValidationRules = (
  contractStartDate?: Date | null
): ValidationRules<Exclude<keyof TenantFormValues, 'id'>> => ({
  [TenantFormFieldNames.LastName]: Yup.string()
    .trim()
    .required('Ce champ est requis')
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(50, 'Le nom ne doit pas dépasser 50 caractères')
    .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le nom ne doit contenir que des lettres'),

  [TenantFormFieldNames.FirstName]: Yup.string()
    .trim()
    .required('Ce champ est requis')
    .min(2, 'Le prénom doit contenir au moins 2 caractères')
    .max(50, 'Le prénom ne doit pas dépasser 50 caractères')
    .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le prénom ne doit contenir que des lettres'),

  [TenantFormFieldNames.BirthDate]: Yup.date()
    .required('Ce champ est requis')
    .max(new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000), 'Le locataire doit être majeur')
    .min(new Date(Date.now() - 120 * 365 * 24 * 60 * 60 * 1000), 'Veuillez saisir une date de naissance valide')
    .typeError('Veuillez saisir une date valide'),

  [TenantFormFieldNames.Phone]: Yup.string()
    .trim()
    .nullable()
    // Champ optionnel : sans excludeEmptyString, la chaîne vide échoue au regex
    // et bloque le formulaire sans erreur visible tant que le champ n'est pas touché.
    .matches(/^(\+33|0)[1-9](\d{8})$/, {
      message: 'Veuillez saisir un numéro de téléphone français valide',
      excludeEmptyString: true,
    }),

  [TenantFormFieldNames.Email]: Yup.string()
    .trim()
    .nullable()
    .email('Veuillez saisir une adresse email valide')
    .max(100, 'L\'adresse email ne doit pas dépasser 100 caractères'),

  [TenantFormFieldNames.Civility]: Yup.string()
    .required('Ce champ est requis')
    .trim(),

  [TenantFormFieldNames.Guarantor]: Yup.object(),

  [TenantFormFieldNames.ProfessionalSituation]: Yup.string()
    .trim()
    .when(TenantFormFieldNames.CheckInDate, {
      // Un locataire en place (≥ 6 mois) n'a plus de dossier pro/revenus :
      // la situation professionnelle n'est plus demandée.
      is: (checkInDate: string) =>
        getTenantSeniorityStatus(checkInDate, contractStartDate) === TenantSeniorityStatusEnum.InPlace,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required('Ce champ est requis'),
    }),

  [TenantFormFieldNames.CheckInDate]: Yup.date()
    .required('Ce champ est requis')
    .min(new Date(2000, 0, 1), 'La date d\'entrée ne peut pas être antérieure à l\'an 2000')
    .typeError('Veuillez saisir une date valide'),

  [TenantFormFieldNames.CheckOutDate]: Yup.date()
    .nullable()
    .min(Yup.ref(TenantFormFieldNames.CheckInDate), 'La date de sortie doit être postérieure à la date d\'entrée')
    .typeError('Veuillez saisir une date valide'),

  [TenantFormFieldNames.RentPaymentsUpToDate]: Yup.boolean()
    .when(TenantFormFieldNames.CheckInDate, {
      is: (checkInDate: string) =>
        getTenantSeniorityStatus(checkInDate, contractStartDate) === TenantSeniorityStatusEnum.InPlace,
      then: (schema) => schema.required('Veuillez indiquer si le locataire est à jour de ses loyers'),
      otherwise: (schema) => schema.nullable(),
    }),

  [TenantFormFieldNames.Incomes]: Yup.array()
    .when(TenantFormFieldNames.CheckInDate, {
      // Revenus exigés uniquement pour un NOUVEAU locataire : un locataire en place
      // n'a plus de dossier revenus, et un locataire en zone refusée est refusé quoi qu'il arrive.
      // La validation des lignes (.of) doit être exemptée aussi : la section est masquée
      // dans ces statuts, une ligne résiduelle incomplète bloquerait le formulaire
      // sans aucune erreur visible.
      is: (checkInDate: string) =>
        getTenantSeniorityStatus(checkInDate, contractStartDate) !== TenantSeniorityStatusEnum.InPlace &&
        getTenantSeniorityStatus(checkInDate, contractStartDate) !== TenantSeniorityStatusEnum.RefusedPeriod &&
        getTenantSeniorityStatus(checkInDate, contractStartDate) !== TenantSeniorityStatusEnum.FutureEntry,
      then: (schema) => schema
        .of(
          Yup.object().shape({
            amount: Yup.number()
              .required('Le montant est requis')
              .positive('Le montant doit être positif')
              .max(1000000, 'Le montant ne peut pas dépasser 1 000 000 €'),
            incomeType: Yup.string()
              .required('Le type de revenu est requis'),
          })
        )
        .min(1, 'Au moins 1 revenu requis'),
    })
});

/** Règles avec la date du jour comme référence (compat — préférer buildTenantValidationRules). */
export const tenantValidationRules = buildTenantValidationRules();
